/**
 * Who a dispatch screen, query, preview or pending command belongs to (batch 8 T12c1, #3033).
 *
 * Everything the dispatch screens hold (cached answers, a reviewed document, a command of unknown outcome) belongs to ONE signed-in
 * session of one user in one tenant, with the permissions that session holds at this moment:
 *
 *  - `owner` = tenant + user + `sessionFamilyId` (a session restored from before families existed is one legacy session). It is
 *    the persistent identity of a pending command: it survives a reload and a token rotation, not a new login, a logout or another
 *    tenant or user.
 *  - `epoch` (`sessionEpoch`) is the in-memory generation. It is not persisted; it only fences work in flight in this page.
 *  - permissions are read as primitives so a downgrade re-renders in the same pass, and they are read from the store again at the
 *    moment of every dispatch and of every late answer. A locked portal is not active: nothing is read or sent while it is locked.
 *
 * The dispatch permission and the read permission are different: `purchase_orders.read` shows state, `purchase_orders.dispatch`
 * shows content and acts. One never implies the other.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

export const DISPATCH_PERMISSION = 'purchase_orders.dispatch';
export const ORDER_READ_PERMISSION = 'purchase_orders.read';
export const INVENTORY_READ_PERMISSION = 'inventory.read';
export const INVENTORY_UPDATE_PERMISSION = 'inventory.update';

export const READ_PERMISSIONS: readonly string[] = [ORDER_READ_PERMISSION];
export const DISPATCH_PERMISSIONS: readonly string[] = [DISPATCH_PERMISSION];
export const SETTINGS_READ_PERMISSIONS: readonly string[] = [INVENTORY_READ_PERMISSION];
/** Both are needed: they are two separate guards on the server. */
export const SETTINGS_WRITE_PERMISSIONS: readonly string[] = [
  INVENTORY_UPDATE_PERMISSION,
  DISPATCH_PERMISSION,
];

export const LEGACY_FAMILY = 'legacy-session';

export interface DispatchOwner {
  readonly tenantId: string;
  readonly userId: string;
  readonly sessionFamilyId: string;
}

export interface DispatchAccess {
  /** Identity of the signed-in session (also while locked). `null`: nobody is signed in. */
  readonly owner: DispatchOwner | null;
  readonly epoch: number;
  /** Signed in and not locked: the only state in which anything may be read or sent. */
  readonly active: boolean;
  /** `purchase_orders.read`: state of the dispatches. */
  readonly canReadStatus: boolean;
  /** `purchase_orders.dispatch`: preview, content, history, enqueue, cancel, resolve. */
  readonly canDispatch: boolean;
  /** `inventory.read`: the supplier's dispatch settings. */
  readonly canReadSettings: boolean;
  /** `inventory.update` AND `purchase_orders.dispatch`. */
  readonly canWriteSettings: boolean;
}

export const ownerEquals = (a: DispatchOwner | null, b: DispatchOwner | null): boolean =>
  a !== null &&
  b !== null &&
  a.tenantId === b.tenantId &&
  a.userId === b.userId &&
  a.sessionFamilyId === b.sessionFamilyId;

/** A string that is equal exactly for equal owners. */
export const ownerId = (owner: DispatchOwner): string =>
  JSON.stringify([owner.tenantId, owner.userId, owner.sessionFamilyId]);

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

function buildOwner(
  tenantId: unknown,
  userId: unknown,
  family: unknown,
  authenticated: boolean
): DispatchOwner | null {
  if (!authenticated) return null;
  if (
    typeof tenantId !== 'string' ||
    tenantId === '' ||
    typeof userId !== 'string' ||
    userId === ''
  )
    return null;
  return {
    tenantId,
    userId,
    sessionFamilyId: typeof family === 'string' && family !== '' ? family : LEGACY_FAMILY,
  };
}

interface Holds {
  status: boolean;
  dispatch: boolean;
  settingsRead: boolean;
  settingsUpdate: boolean;
}

function buildAccess(
  owner: DispatchOwner | null,
  epoch: number,
  locked: boolean,
  holds: Holds
): DispatchAccess {
  const active = owner !== null && !locked;
  return {
    owner,
    epoch,
    active,
    canReadStatus: active && holds.status,
    canDispatch: active && holds.dispatch,
    canReadSettings: active && holds.settingsRead,
    canWriteSettings: active && holds.settingsUpdate && holds.dispatch,
  };
}

/** The access of the store as it is at this moment. Use it at the moment of an action, never a captured copy. */
export function readDispatchAccess(state: AuthSnapshot = useAuthStore.getState()): DispatchAccess {
  return buildAccess(
    buildOwner(state.user?.tenantId, state.user?.id, state.sessionFamilyId, state.isAuthenticated),
    state.sessionEpoch,
    state.isLocked,
    {
      status: state.hasPermission(ORDER_READ_PERMISSION),
      dispatch: state.hasPermission(DISPATCH_PERMISSION),
      settingsRead: state.hasPermission(INVENTORY_READ_PERMISSION),
      settingsUpdate: state.hasPermission(INVENTORY_UPDATE_PERMISSION),
    }
  );
}

/** The reactive access. Every input is a primitive, so an owner or permission change re-renders in the same pass. */
export function useDispatchAccess(): DispatchAccess {
  const tenantId = useAuthStore((state) => state.user?.tenantId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const family = useAuthStore((state) => state.sessionFamilyId);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const locked = useAuthStore((state) => state.isLocked);
  const epoch = useAuthStore((state) => state.sessionEpoch);
  const status = useAuthStore((state) => state.hasPermission(ORDER_READ_PERMISSION));
  const dispatch = useAuthStore((state) => state.hasPermission(DISPATCH_PERMISSION));
  const settingsRead = useAuthStore((state) => state.hasPermission(INVENTORY_READ_PERMISSION));
  const settingsUpdate = useAuthStore((state) => state.hasPermission(INVENTORY_UPDATE_PERMISSION));
  return useMemo(
    () =>
      buildAccess(buildOwner(tenantId, userId, family, authenticated), epoch, locked, {
        status,
        dispatch,
        settingsRead,
        settingsUpdate,
      }),
    [
      tenantId,
      userId,
      family,
      authenticated,
      locked,
      epoch,
      status,
      dispatch,
      settingsRead,
      settingsUpdate,
    ]
  );
}

/**
 * Identity of everything one signed-in owner may see and do: owner, generation and rights. Used as a React `key`, so a different
 * owner, a new login of the same user or any change of rights remounts the subtree in the very render that sees it.
 */
export function accessKey(access: DispatchAccess): string {
  if (access.owner === null || !access.active) return 'none';
  return JSON.stringify([
    access.owner.tenantId,
    access.owner.userId,
    access.owner.sessionFamilyId,
    access.epoch,
    access.canReadStatus,
    access.canDispatch,
    access.canReadSettings,
    access.canWriteSettings,
  ]);
}

// ---------------------------------------------------------------------------
// Refusals and fences
// ---------------------------------------------------------------------------

/** Refused before any request: the caller lacks what the endpoint needs. */
export class DispatchAccessError extends Error {
  constructor(readonly required: readonly string[]) {
    super(`Missing permission: ${required.join(', ')}`);
    this.name = 'DispatchAccessError';
  }
}

/** Refused before any request: the session, tenant, user or generation is no longer the one the action began under. */
export class OwnerChangedError extends Error {
  constructor() {
    super('The session changed before the request was sent');
    this.name = 'OwnerChangedError';
  }
}

export type Outcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: unknown };

/** Runs `call` and returns its outcome instead of throwing, so a late answer can be carried to the right place. */
export async function settle<T>(call: () => Promise<T>): Promise<Outcome<T>> {
  try {
    return { ok: true, value: await call() };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * A request WAS sent, and its answer arrived after the session, tenant, user, generation or a needed permission had changed.
 * The answer must not reach the screen, the cache or a notification; it is carried only so the owner of a command can still
 * learn what the server decided.
 */
export class LateResultError extends Error {
  constructor(readonly outcome: Outcome<unknown>) {
    super('The result arrived after the session changed');
    this.name = 'LateResultError';
  }
}

/** A refusal of the client that happened before anything was sent. */
export const isLocalRefusal = (error: unknown): boolean =>
  error instanceof DispatchAccessError || error instanceof OwnerChangedError;
/** Errors a screen must swallow silently: they concern an owner that is no longer on screen. */
export const isSessionCancel = (error: unknown): boolean =>
  error instanceof OwnerChangedError || error instanceof LateResultError;

/** What an in-flight action started under, checked again before it may change anything. */
export interface DispatchFence {
  readonly owner: DispatchOwner;
  readonly epoch: number;
  readonly permissions: readonly string[];
}

/**
 * The fence of NOW, for an action that began under `expected` (the access of the render that offered it). Throws before anything is
 * sent when that owner or generation is not the live one, or when a needed permission is no longer held.
 */
export function takeFence(
  expected: Pick<DispatchAccess, 'owner' | 'epoch'>,
  permissions: readonly string[]
): DispatchFence {
  if (expected.owner === null) throw new DispatchAccessError(['session']);
  const state = useAuthStore.getState();
  const live = readDispatchAccess(state);
  if (
    !live.active ||
    live.owner === null ||
    !ownerEquals(live.owner, expected.owner) ||
    live.epoch !== expected.epoch
  ) {
    throw new OwnerChangedError();
  }
  const missing = permissions.filter((permission) => !state.hasPermission(permission));
  if (missing.length > 0) throw new DispatchAccessError(missing);
  return { owner: live.owner, epoch: live.epoch, permissions };
}

/** Whether what a fence was taken under still holds: same owner, same generation, unlocked, permissions still held. */
export function fenceHolds(fence: DispatchFence): boolean {
  const state = useAuthStore.getState();
  const live = readDispatchAccess(state);
  return (
    live.active &&
    ownerEquals(live.owner, fence.owner) &&
    live.epoch === fence.epoch &&
    fence.permissions.every((permission) => state.hasPermission(permission))
  );
}

/**
 * One request of the session that offered it. The owner and the permissions are checked again from the live store just before the
 * request leaves (a refusal before anything is sent) and again when the answer arrives: an answer that outlived its owner is carried
 * in a `LateResultError`, never returned.
 */
export async function runOwned<T>(
  access: Pick<DispatchAccess, 'owner' | 'epoch'>,
  permissions: readonly string[],
  call: () => Promise<T>
): Promise<T> {
  const fence = takeFence(access, permissions);
  const outcome = await settle(call);
  if (!fenceHolds(fence)) throw new LateResultError(outcome);
  if (!outcome.ok) throw outcome.error;
  return outcome.value;
}
